import { useState } from 'react';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import {
  AntModal,
  Button,
  Comment,
  Footer,
  TitleBar,
} from '../../../../components';
import QueryErrorBoundary from '../../../../components/error-handling';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import FinancialData from '../../../../components/common/FinancialData';
import { message, TableColumnsType } from 'antd';
import PercentageFormatter from '../../../../components/common/PercentageFormatter';
import DependenciesTitle from '../../../../components/common/DependenciesTitle';
import { tableSortingMethods } from '../../../../utils';
import { useNavigate, useParams } from 'react-router';
import { useUserContext } from '../../../../context/PermissionsContext';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  GET_LOCATION_IT_APPS_MATRIX,
  SET_LOCATION_IT_APP_DEPENDENCY_PERCENTAGE,
} from './queries';
import {
  ApplicationDependency,
  DivisionDependency,
  DivisionType,
} from '../../enterprise-applications';
import { formatTree, pushAllOtherToBottom } from '../../utils/formatTree';
import { GET_LOCATION_NAME } from '../../../../graphql/utils/queries';
import { Location } from '../../../../interfaces/RiskHub';
import { MappingCommentForm } from '../../components';
import { calculateTableColumnWidth } from '../../../../utils/calculateTableColumnWidth';

type FetchedLocation = {
  findLocationByUuid: Location;
};

type FetchedMappedApplications = {
  getLocationItApllicationsDashboard: DivisionType;
};

const LocationApplicationMapping = () => {
  const { locationId } = useParams();

  const { data: locationData, error: locationError } =
    useQuery<FetchedLocation>(GET_LOCATION_NAME, {
      variables: {
        uuid: locationId,
      },
    });

  const locationName = locationData?.findLocationByUuid.locationName ?? '';

  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { canViewFinancials, canEdit } = useUserContext();

  const { data, loading, error } = useQuery<FetchedMappedApplications>(
    GET_LOCATION_IT_APPS_MATRIX,
    {
      variables: {
        data: locationId,
      },
      skip: !locationId,
    },
  );

  const [
    updateApplicationMapping,
    { loading: updateApplicationMappingLoading },
  ] = useMutation(SET_LOCATION_IT_APP_DEPENDENCY_PERCENTAGE, {
    refetchQueries: ['GetLocationItApllicationsDashboard'],
  });

  const ITAppsData = data?.getLocationItApllicationsDashboard?.divisions || [];

  const loadingStates = loading || updateApplicationMappingLoading;

  const ITAppsDataFormatted = formatTree(ITAppsData) ?? [];

  const orderedITAppsData = pushAllOtherToBottom(ITAppsDataFormatted);

  const ITApplications = orderedITAppsData[0]?.applications ?? [];

  const applicationsColumns: TableColumnsType<DivisionDependency> =
    ITApplications?.map((appGlobal, index: number) => ({
      title: (
        <DependenciesTitle name={appGlobal.name} comment={appGlobal.comment} />
      ),
      dataIndex: 'applications',
      key: appGlobal.uuid,
      colIndex: index,
      editable: true,
      width: calculateTableColumnWidth(appGlobal.name),
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : undefined,
      showSorterTooltip: false,
      render: (_, record) => {
        const applications = [...record.applications];

        const actualApplication = applications.find(
          (app) => app.uuid === appGlobal.uuid,
        );

        const { percentage, revenue } = actualApplication?.value ?? {
          percentage: 0,
          revenue: 0,
        };

        return (
          <div className="custom-table--column">
            <PercentageFormatter value={percentage} />
            <FinancialData text={revenue ?? 0} />
          </div>
        );
      },
    }));

  const columns: TableColumnsType<DivisionDependency> = [
    {
      title: '',
      width: 250,
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: (
        <span>
          Revenue <br />
          Supported
          <br />
          Annually
        </span>
      ),
      width: 150,
      dataIndex: 'revenue',
      key: 'revenue',
      fixed: 'left',
      align: 'right',
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} formatType="decimal" />}
        </span>
      ),
    },
    ...applicationsColumns,
  ];

  const handleSaveComment = async (uuid: string, updatedComment: string) => {
    const divisionId = orderedITAppsData[0]?.uuid ?? '';
    try {
      const response = await updateApplicationMapping({
        variables: {
          data: {
            itApplicationValues: [
              {
                description: updatedComment,
                itApplicationsUuid: uuid,
              },
            ],
            dependencyType: 'DIVISION',
            dependencyUuid: divisionId,
            locationUuid: locationId,
          },
        },
      });
      if (response?.data?.setLocationDependencyPercentage)
        message.success('Changes Saved');
    } catch (e) {
      if (e instanceof ApolloError) {
        message.error(e.message);
        return;
      }
      message.error('Failed to Save Changes');
    }
  };

  const modifyVariables = ({ currentRecord }: ModifyVariablesArgs) => {
    let dependencyType;

    if (currentRecord?.__typename === 'DivisionDependency') {
      dependencyType = 'DIVISION';
    } else if (currentRecord?.__typename === 'ProductGroupDependency') {
      dependencyType = 'PRODUCT_GROUP';
    } else {
      dependencyType = 'PRODUCT';
    }

    return {
      dependencyType,
      dependencyUuid: currentRecord?.uuid,
      locationUuid: locationId,
    };
  };

  const matrixConfig = {
    propertyToMap: 'applications',
  };

  return (
    <QueryErrorBoundary error={locationError}>
      <TitleUpdater title="IT Application Mapping" />
      <TitleBar
        title="IT Application Mapping"
        subtitle={`Location Level Value Streams / ${locationName}`}
        brief="In the matrix, enter the degree to which the various revenue streams at this location depend on the listed IT applications."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={orderedITAppsData}
          updateGQL={updateApplicationMapping}
          loading={loadingStates}
          customStyles="nested-columns--custom custom-table--dependency-mapping"
          editable
          inlineEditing
          editableOptions={{
            update: canEdit,
          }}
          isMatrix
          matrixConfig={matrixConfig}
          modifyVariables={modifyVariables}
          scroll={{ x: 800, y: 540 }}
          tableSize="small"
          matrixType="applications"
          extendOptions={
            <div className="w-full flex items-center gap-2 min-w-max">
              <button
                onClick={() => setIsModalOpen(true)}
                className="text-blue underline hover:cursor-pointer font-normal"
                aria-label="Add / Edit Commentary"
              >
                Add / Edit Commentary
              </button>
              <Comment />
            </div>
          }
        />
      </QueryErrorBoundary>
      <AntModal
        title="Add Commentary"
        openModal={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        <MappingCommentForm<ApplicationDependency>
          title={'IT Applications'}
          data={ITApplications}
          onSave={handleSaveComment}
        />
      </AntModal>
      <div className="flex items-center justify-between">
        <Footer
          backPath={`/cyber/location-level/${locationId}/it-applications/selection`}
        />
        <Button
          type="primary"
          className="min-w-[188px] text-sm font-bold px-1 text-primary-100 py-0.5"
          onClick={() =>
            navigate(
              `/cyber/location-level/${locationId}/it-applications/summary`,
            )
          }
        >
          Summary
        </Button>
      </div>
    </QueryErrorBoundary>
  );
};

export default LocationApplicationMapping;
